import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MentionInput - A textarea with @mention autocomplete (BRUTAL STYLE)
 */
const MentionInput = ({ 
  value = '', 
  onChange, 
  onSubmit, 
  placeholder = "Type a message...",
  className = "",
  disabled = false,
  rows = 1,
  autoFocus = false
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(searchQuery)}`);
        setSuggestions(response.data);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Error searching users:', error);
        setSuggestions([]);
      }
    };

    const debounce = setTimeout(searchUsers, 150);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const findMentionAtCursor = useCallback((text, cursorPos) => {
    const textBeforeCursor = text.slice(0, cursorPos);
    const match = textBeforeCursor.match(/@(\w*)$/);
    
    if (match) {
      return {
        query: match[1],
        startPos: cursorPos - match[0].length,
        endPos: cursorPos
      };
    }
    return null;
  }, []);

  const handleChange = (e) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;
    
    onChange(newValue);
    setCursorPosition(newCursorPos);
    
    const mention = findMentionAtCursor(newValue, newCursorPos);
    if (mention) {
      setSearchQuery(mention.query);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSearchQuery('');
    }
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        selectSuggestion(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    } else if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  const selectSuggestion = (user) => {
    const mention = findMentionAtCursor(value, cursorPosition);
    if (!mention) return;
    
    const newValue = 
      value.slice(0, mention.startPos) + 
      `@${user.username} ` + 
      value.slice(mention.endPos);
    
    onChange(newValue);
    setShowSuggestions(false);
    setSearchQuery('');
    
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mention.startPos + user.username.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
      }
    }, 150);
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        autoFocus={autoFocus}
        className={`w-full bg-zom-black border-2 border-zom-white px-4 py-3 text-zom-white placeholder-zom-white/40 focus:outline-none focus:border-zom-yellow resize-none font-body ${className}`}
      />
      
      {/* Suggestions dropdown - BRUTAL */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute bottom-full left-0 right-0 mb-2 bg-zom-black border-3 border-zom-yellow shadow-brutal overflow-hidden z-50 max-h-48 overflow-y-auto"
        >
          {suggestions.map((user, index) => (
            <button
              key={user.id}
              onClick={() => selectSuggestion(user)}
              className={`w-full px-4 py-2 text-left flex items-center gap-3 transition-colors border-b border-zom-white/20 last:border-0 ${
                index === selectedIndex 
                  ? 'bg-zom-yellow text-zom-black' 
                  : 'hover:bg-zom-blue text-zom-white'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center text-sm font-heading border-2 ${
                index === selectedIndex ? 'bg-zom-black text-zom-yellow border-zom-black' : 'bg-zom-yellow text-zom-black border-zom-black'
              }`}>
                {user.username[0].toUpperCase()}
              </div>
              <div>
                <div className="font-heading text-sm">@{user.username.toUpperCase()}</div>
                {user.display_name && (
                  <div className="text-xs opacity-70 font-body">{user.display_name}</div>
                )}
              </div>
              {user.roles?.includes('writer') && (
                <span className={`ml-auto text-[10px] px-2 py-0.5 font-heading border ${
                  index === selectedIndex ? 'bg-zom-blue text-zom-white border-zom-black' : 'bg-zom-blue text-zom-white border-zom-white'
                }`}>WRITER</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
